import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';

import { ConnectionConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory } from '@frontend/sports/common/core/feature/logging';
import { CriticalErrorService } from '@frontend/sports/critical-error/feature';
import { Observable, tap } from 'rxjs';

export const connectionLostInterceptor: HttpInterceptorFn = (req, next) => inject(ConnectionLostInterceptor).intercept(req, next);

@Injectable({
    providedIn: 'root',
})
export class ConnectionLostInterceptor {
    private injector = inject(Injector);

    private asyncRetriesCount = 0;
    private maxRetriesCount: number | undefined = undefined;

    intercept(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
        return next(req).pipe(
            tap({
                next: (event) => {
                    if (event instanceof HttpResponse) {
                        this.response(event);
                    }
                },
                error: (error: unknown) => {
                    if (error instanceof HttpErrorResponse) {
                        this.responseError(error);
                    }
                },
            }),
        );
    }

    response(response: HttpResponse<unknown>): HttpResponse<unknown> {
        const connectionConfig = this.injector.get(ConnectionConfig);
        const criticalErrorService = this.injector.get(CriticalErrorService);
        const loggerFactory: LoggerFactory = this.injector.get(LoggerFactory);
        const logger = loggerFactory.getLogger('ConnectionLostInterceptor');
        const sitecore = this.injector.get(Sitecore);
        const cdsUrl = connectionConfig.cdsFrontendUrl ?? connectionConfig.cdsUrlBase;
        sitecore.whenReady.subscribe(() => (this.maxRetriesCount = parseInt(sitecore.globalMessages['CriticalErrorsRetryCount'])));

        if (response.url && response.url.includes(cdsUrl)) {
            if (criticalErrorService.isCriticalErrorVisible) {
                logger.error('Recovered back from critical error on AJAX calls');
            }
            criticalErrorService.hideCriticalError();
            this.asyncRetriesCount = 0;
        }

        return response;
    }

    responseError(error: HttpErrorResponse): void {
        const connectionConfig = this.injector.get(ConnectionConfig);
        const criticalErrorService = this.injector.get(CriticalErrorService);
        const loggerFactory = this.injector.get(LoggerFactory);
        const logger = loggerFactory.getLogger('ConnectionLostInterceptor');
        const sitecore = this.injector.get(Sitecore);
        const cdsUrl = connectionConfig.cdsFrontendUrl ?? connectionConfig.cdsUrlBase;
        sitecore.whenReady.subscribe(() => (this.maxRetriesCount = parseInt(sitecore.globalMessages['CriticalErrorsRetryCount'])));
        if (error.url && error.url.includes(cdsUrl)) {
            this.asyncRetriesCount++;

            if (this.maxRetriesCount !== undefined && this.asyncRetriesCount > this.maxRetriesCount) {
                criticalErrorService.showCriticalError();
                logger.error(error, 'Too many failed AJAX requests, showing the critical error page');
            }
            logger.error(error, 'Failed connection to external endpoin');
        }
    }
}
